/**
 * Менеджер истории изменений и отката (Undo / Redo / Snapshots) для SPD+
 */

class HistoryManager {
    constructor(maxSize = 50) {
        this.maxSize = maxSize;
        this.undoStack = [];
        this.redoStack = [];
        this.listeners = new Set();
    }

    /**
     * Подписка на изменения состояния истории (для UI кнопок Undo/Redo)
     */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    notify() {
        const state = this.getState();
        this.listeners.forEach(fn => {
            try { fn(state); } catch (e) { console.error(e); }
        });
    }

    getState() {
        return {
            canUndo: this.undoStack.length > 0,
            canRedo: this.redoStack.length > 0,
            undoCount: this.undoStack.length,
            redoCount: this.redoStack.length,
            lastAction: this.undoStack[this.undoStack.length - 1] || null
        };
    }

    /**
     * Добавление новой операции в историю
     * @param {Object} action
     * @param {string} action.type - 'difference', 'union', 'split', 'delete_duplicate', 'merge_duplicate', etc.
     * @param {string} action.description - Человекочитаемое описание
     * @param {Array<Object>} action.before - Состояние объектов до операции [{ layer, id, feature, isDeleted }]
     * @param {Array<Object>} action.after - Состояние объектов после операции [{ layer, id, feature, isCreated }]
     */
    pushAction(action) {
        if (!action || !action.before || !action.after) return;

        const record = {
            id: 'act_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
            timestamp: new Date(),
            type: action.type || 'mutation',
            description: action.description || 'Топологическая операция',
            before: JSON.parse(JSON.stringify(action.before)),
            after: JSON.parse(JSON.stringify(action.after))
        };

        this.undoStack.push(record);
        if (this.undoStack.length > this.maxSize) {
            this.undoStack.shift();
        }

        // Очищаем стек повтора при новом действии
        this.redoStack = [];
        this.notify();
        return record;
    }

    /**
     * Откат последнего действия (Undo)
     * @returns {Object|null} Инструкции по восстановлению состояния { description, restoreItems: [...] }
     */
    undo() {
        if (this.undoStack.length === 0) return null;

        const action = this.undoStack.pop();
        this.redoStack.push(action);
        this.notify();

        return {
            actionId: action.id,
            description: action.description,
            // Для отката применяем состояние "before"
            itemsToRestore: action.before,
            itemsToRemove: action.after.filter(item => item.isCreated).map(item => ({ layer: item.layer, id: item.id }))
        };
    }

    /**
     * Повтор отмененного действия (Redo)
     * @returns {Object|null} Инструкции по повторному применению { description, applyItems: [...] }
     */
    redo() {
        if (this.redoStack.length === 0) return null;

        const action = this.redoStack.pop();
        this.undoStack.push(action);
        this.notify();

        return {
            actionId: action.id,
            description: action.description,
            // Для повтора применяем состояние "after"
            itemsToApply: action.after,
            itemsToRestore: action.after,
            itemsToRemove: action.before.filter(item => item.isCreated).map(item => ({ layer: item.layer, id: item.id }))
        };
    }

    /**
     * Очистка всей истории
     */
    clear() {
        this.undoStack = [];
        this.redoStack = [];
        this.notify();
    }
}

// Экспорт синглтона
export const historyManager = new HistoryManager();
export default historyManager;
